import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import QrScanner from "@/components/QrScanner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plane, Ship, PackagePlus, ArrowRight, Truck, Weight, Box, Plus, Trash2, MapPin, Calculator, Lock, QrCode } from "lucide-react";
import { WarehouseShopShip } from "@/components/WarehouseShopShip";
import { SecurityPanel } from "@/components/SecurityPanel";
import { PartnerGovernanceCard } from "@/components/AdminGovernance";
import { curSym, shipCur } from "@/lib/currency";

export default function PartnerDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "receive";
  const [scanOpen, setScanOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [expectedP, setExpectedP] = useState([]);
  const [ssWarehouses, setSsWarehouses] = useState([]);
  const [hubColls, setHubColls] = useState([]);
  const [recv, setRecv] = useState({ shipment_id: "", supplier_name: "", cartons: 1, weight_kg: 0, length_cm: 0, width_cm: 0, height_cm: 0, condition: "good", notes: "" });

  const load = () => {
    api.get("/partner/profile").then(({ data }) => { setProfile(data.partner); setRoutes(data.routes); });
    api.get("/shipments").then(({ data }) => setShipments(data));
    api.get("/partner/expected").then(({ data }) => setExpectedP(data));
    api.get("/warehouse/shopship/warehouses").then(({ data }) => setSsWarehouses(data)).catch(() => {});
    api.get("/partner/hub-collections").then(({ data }) => setHubColls(data)).catch(() => {});
  };
  useEffect(load, []);

  const receive = async () => {
    if (!recv.shipment_id || !recv.supplier_name) { toast.error("Référence et fournisseur requis"); return; }
    try {
      await api.post("/partner/receive", {
        ...recv, cartons: Number(recv.cartons), weight_kg: Number(recv.weight_kg),
        length_cm: Number(recv.length_cm), width_cm: Number(recv.width_cm), height_cm: Number(recv.height_cm), photos: [],
      });
      toast.success("Colis enregistré");
      setRecv({ shipment_id: "", supplier_name: "", cartons: 1, weight_kg: 0, length_cm: 0, width_cm: 0, height_cm: 0, condition: "good", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const setFinal = async (sid) => { await api.post(`/partner/shipments/${sid}/final-price`); toast.success("Prix final calculé et envoyé au client"); load(); };
  const ship = async (sid) => {
    const ref = prompt("Référence de preuve (AWB / Bill of Lading) :");
    if (!ref) return;
    await api.post(`/partner/shipments/${sid}/ship`, { reference: ref, type: "AWB/BL" }); toast.success("Expédition confirmée"); load();
  };
  const advance = async (sid, key) => { await api.post(`/partner/shipments/${sid}/status`, { key }); toast.success("Statut mis à jour"); load(); };

  const onQrScan = (text) => {
    const ref = (text || "").trim();
    const match = shipments.find((s) => s.reference === ref);
    if (match) { setRecv((r) => ({ ...r, shipment_id: match.id })); toast.success(`${match.reference} sélectionnée`); }
    else toast.error(`Référence inconnue : ${ref}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-accent">{t("dash.partner")}</div>
            <h1 className="font-heading font-extrabold text-3xl tracking-tight flex items-center gap-3">{profile?.company_name}
              {profile && <Badge className={`rounded-sm ${profile.verified ? "bg-emerald-600 text-white" : "bg-amber-100 text-amber-700"}`}>{profile.verified ? "Vérifié" : "En attente de vérification"}</Badge>}
            </h1>
          </div>
        </div>

        <Tabs value={tab} onValueChange={(v) => setSearchParams({ tab: v })} className="mt-6">
          <TabsList className="rounded-sm flex-wrap h-auto">
            <TabsTrigger value="receive" data-testid="ptab-receive">{t("partner.dash.newPackages")}</TabsTrigger>
            {ssWarehouses.length > 0 && <TabsTrigger value="shopship" data-testid="ptab-shopship">Shop &amp; Ship</TabsTrigger>}
            <TabsTrigger value="shipments" data-testid="ptab-shipments">{t("partner.dash.shipments")}</TabsTrigger>
            <TabsTrigger value="hubcollect" data-testid="ptab-hubcollect">Collectes Hub{hubColls.length > 0 ? ` (${hubColls.length})` : ""}</TabsTrigger>
            <TabsTrigger value="routes" data-testid="ptab-routes">{t("partner.dash.routes")}</TabsTrigger>
            <TabsTrigger value="profile" data-testid="ptab-profile">{t("partner.dash.profile")}</TabsTrigger>
          </TabsList>

          {/* RECEIVE */}
          <TabsContent value="receive" className="mt-5">
            {expectedP.filter((e) => e.status !== "received").length > 0 && (
              <div className="mb-5 border border-accent/40 bg-accent/5 p-4">
                <h3 className="font-heading font-bold text-sm">Colis attendus ({expectedP.filter((e) => e.status !== "received").length})</h3>
                <div className="mt-2 space-y-1.5">
                  {expectedP.filter((e) => e.status !== "received").map((e) => (
                    <div key={e.id} data-testid={`partner-expected-${e.id}`} className="flex flex-wrap items-center justify-between gap-2 text-sm border border-border bg-card p-2">
                      <span><strong>{e.supplier_name}</strong> · {e.reference} · {e.cartons_expected} carton(s){e.tracking_number && ` · Tracking: ${e.tracking_number}`}</span>
                      <Button size="sm" variant="outline" className="rounded-sm" data-testid={`prefill-${e.id}`}
                        onClick={() => setRecv({ ...recv, shipment_id: e.shipment_id, supplier_name: e.supplier_name, cartons: e.cartons_expected })}>
                        Réceptionner
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="border border-border bg-card p-5">
                <h2 className="font-heading font-bold text-lg flex items-center gap-2"><PackagePlus className="h-5 w-5 text-accent" /> {t("partner.dash.receive")}</h2>
                <div className="mt-4 space-y-3">
                  <div><Label>{t("partner.dash.scan")}</Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Select value={recv.shipment_id} onValueChange={(v) => setRecv({ ...recv, shipment_id: v })}>
                          <SelectTrigger data-testid="receive-shipment" className="rounded-sm"><SelectValue placeholder="Référence expédition" /></SelectTrigger>
                          <SelectContent>{shipments.map((s) => <SelectItem key={s.id} value={s.id}>{s.reference} · {s.dest_city}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <Button type="button" variant="outline" className="rounded-sm gap-1 shrink-0" data-testid="open-qr-scanner" onClick={() => setScanOpen(true)}>
                        <QrCode className="h-4 w-4" /> {t("scan.open")}
                      </Button>
                    </div>
                  </div>
                  <div><Label>{t("ship.supplierName")}</Label><Input data-testid="receive-supplier" className="rounded-sm" value={recv.supplier_name} onChange={(e) => setRecv({ ...recv, supplier_name: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("partner.dash.cartonsCount")}</Label><Input data-testid="receive-cartons" type="number" className="rounded-sm" value={recv.cartons} onChange={(e) => setRecv({ ...recv, cartons: e.target.value })} /></div>
                    <div><Label>{t("partner.dash.weight")}</Label><Input data-testid="receive-weight" type="number" className="rounded-sm" value={recv.weight_kg} onChange={(e) => setRecv({ ...recv, weight_kg: e.target.value })} /></div>
                  </div>
                  <div><Label>{t("partner.dash.dims")} (L × l × H)</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Input data-testid="receive-length" type="number" placeholder="L" className="rounded-sm" value={recv.length_cm} onChange={(e) => setRecv({ ...recv, length_cm: e.target.value })} />
                      <Input type="number" placeholder="l" className="rounded-sm" value={recv.width_cm} onChange={(e) => setRecv({ ...recv, width_cm: e.target.value })} />
                      <Input type="number" placeholder="H" className="rounded-sm" value={recv.height_cm} onChange={(e) => setRecv({ ...recv, height_cm: e.target.value })} />
                    </div>
                  </div>
                  <div><Label>Notes / état</Label><Textarea className="rounded-sm" value={recv.notes} onChange={(e) => setRecv({ ...recv, notes: e.target.value })} /></div>
                  <Button data-testid="receive-submit" className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" onClick={receive}>Enregistrer la réception</Button>
                </div>
              </div>
              <div className="border border-border bg-card p-5">
                <h3 className="font-heading font-bold">Expéditions en attente de réception</h3>
                <div className="mt-3 space-y-2">
                  {shipments.filter((s) => ["created", "receiving"].includes(s.status)).map((s) => (
                    <div key={s.id} className="border border-border p-3 text-sm flex items-center justify-between">
                      <span className="font-medium">{s.reference} · {s.origin_city} → {s.dest_city}</span>
                      <span className="text-muted-foreground">{(s.packages || []).length} colis</span>
                    </div>
                  ))}
                  {shipments.filter((s) => ["created", "receiving"].includes(s.status)).length === 0 && <p className="text-sm text-muted-foreground">—</p>}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* SHOP & SHIP (entrepôt) */}
          {ssWarehouses.length > 0 && (
            <TabsContent value="shopship" className="mt-5">
              <WarehouseShopShip />
            </TabsContent>
          )}

          {/* SHIPMENTS */}
          <TabsContent value="shipments" className="mt-5 space-y-3">
            {shipments.map((s) => {
              const Mode = s.mode === "air" ? Plane : Ship;
              const tw = (s.packages || []).reduce((a, p) => a + (p.weight_kg || 0), 0);
              const tc = (s.packages || []).reduce((a, p) => a + (p.cbm || 0), 0);
              return (
                <div key={s.id} data-testid={`pshipment-${s.reference}`} className="border border-border bg-card p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 font-heading font-bold"><Mode className="h-4 w-4 text-accent" /> {s.reference} <span className="text-muted-foreground font-normal">· {s.origin_city} → {s.dest_city}</span></div>
                    <Badge className="rounded-sm">{s.status}</Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><Box className="h-4 w-4" /> {(s.packages || []).length} colis</span>
                    <span className="flex items-center gap-1"><Weight className="h-4 w-4" /> {tw.toFixed(1)} kg</span>
                    <span>{tc.toFixed(3)} CBM</span>
                    {s.final_price != null && <span className="text-accent font-bold">{s.final_price} {curSym(shipCur(s))}</span>}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(s.packages || []).length > 0 && s.final_price == null && <Button size="sm" data-testid={`final-${s.reference}`} className="rounded-sm bg-primary text-white gap-1" onClick={() => setFinal(s.id)}><Calculator className="h-4 w-4" /> {t("partner.dash.setFinal")}</Button>}
                    {s.final_accepted && !["shipped", "arrived", "delivered"].includes(s.status) && <Button size="sm" data-testid={`ship-${s.reference}`} className="rounded-sm bg-accent text-white gap-1" onClick={() => ship(s.id)}><Truck className="h-4 w-4" /> {t("partner.dash.markShipped")}</Button>}
                    {s.status === "shipped" && <Button size="sm" variant="outline" className="rounded-sm" onClick={() => advance(s.id, "arrived")}>{t("partner.dash.markArrived")}</Button>}
                    {s.status === "arrived" && <Button size="sm" variant="outline" className="rounded-sm" onClick={() => advance(s.id, "delivered")}>{t("partner.dash.markDelivered")}</Button>}
                  </div>
                </div>
              );
            })}
            {shipments.length === 0 && <div className="border border-dashed p-12 text-center text-muted-foreground">—</div>}
          </TabsContent>

          {/* COLLECTES HUB COLIS2GO */}
          <TabsContent value="hubcollect" className="mt-5 space-y-3">
            <PartnerGovernanceCard />
            <p className="text-sm text-muted-foreground">Colis en attente de collecte aux Hubs Colis2Go. Confirmez la collecte une fois la marchandise récupérée.</p>
            {hubColls.map((c) => (
              <div key={c.id} data-testid={`hubcoll-${c.reference}`} className="border-2 border-accent/40 bg-card p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-heading font-bold">{c.reference} <span className="text-muted-foreground font-normal">· {c.origin_city} → {c.dest_city}, {c.dest_country}</span></div>
                  <Badge className="rounded-sm bg-amber-100 text-amber-700">À collecter</Badge>
                </div>
                <div className="mt-2 text-sm">
                  <div className="font-semibold">{c.hub?.name}</div>
                  <div className="text-muted-foreground">{c.hub?.address}{c.hub?.hours ? ` · ${c.hub.hours}` : ""}{c.hub?.phone ? ` · ${c.hub.phone}` : ""}</div>
                </div>
                <div className="mt-2 flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span>{(c.packages || []).length} colis</span>
                  <span>{c.total_weight} kg</span>
                  <span>{c.total_cbm} CBM</span>
                  <span>Décision client : {c.hub_decision === "consolidate" ? "Consolider" : "Expédier tel quel"}</span>
                  {c.estimated_price != null && <span className="text-accent font-bold">≈ {c.estimated_price} {curSym(shipCur(c))}</span>}
                </div>
                <Button size="sm" data-testid={`collect-${c.reference}`} className="mt-3 rounded-sm bg-accent hover:bg-accent/90 text-white gap-1"
                  onClick={async () => {
                    try { await api.post(`/partner/hub-collections/${c.id}/collect`); toast.success("Collecte confirmée — calculez le prix final dans Expéditions"); load(); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}><Truck className="h-4 w-4" /> Confirmer la collecte</Button>
              </div>
            ))}
            {hubColls.length === 0 && <div className="border border-dashed p-12 text-center text-muted-foreground">Aucune collecte en attente aux Hubs Colis2Go.</div>}
          </TabsContent>

          {/* ROUTES */}
          <TabsContent value="routes" className="mt-5">
            {!profile?.verified && <p className="mb-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 p-3">Vos routes deviennent actives après vérification de votre compte par l’administrateur.</p>}
            <RouteManager routes={routes} onChange={load} />
          </TabsContent>

          {/* PROFILE */}
          <TabsContent value="profile" className="mt-5">
            {profile && <ProfileForm profile={profile} onSaved={load} />}
            <SecurityPanel />
          </TabsContent>
        </Tabs>
        <QrScanner open={scanOpen} onOpenChange={setScanOpen} onScan={onQrScan} />
      </div>
    </div>
  );
}


function RouteManager({ routes, onChange }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const empty = {
    origin_country: "", origin_city: "", dest_country: "", dest_city: "", mode: "air",
    departures: "", delay_days: "", currency: "EUR",
    min_billable: 5, volumetric_divisor: 6000, special_surcharge: 0, delivery_surcharge: 0, destination_fees: 0,
    min_cbm: 1, doc_fees: 0, customs_fees: 0, pallet_price: 0, carton_price: 0,
    tiers: [{ min: 0, max: 0, price: "" }],
  };
  const [f, setF] = useState(empty);

  const setTier = (i, key, val) => {
    const tiers = f.tiers.map((tr, j) => (j === i ? { ...tr, [key]: val } : tr));
    setF({ ...f, tiers });
  };
  const addTier = () => setF({ ...f, tiers: [...f.tiers, { min: 0, max: 0, price: "" }] });
  const removeTier = (i) => setF({ ...f, tiers: f.tiers.filter((_, j) => j !== i) });

  const add = async () => {
    const tiers = f.tiers
      .filter((tr) => tr.price !== "")
      .map((tr) => ({ min: Number(tr.min) || 0, max: Number(tr.max) || 0, price: Number(tr.price) }));
    if (tiers.length === 0) { toast.error(t("route.tiersRequired")); return; }
    const payload = {
      origin_country: f.origin_country, origin_city: f.origin_city, dest_country: f.dest_country, dest_city: f.dest_city,
      mode: f.mode, departures: f.departures, delay_days: f.delay_days, currency: f.currency,
      min_billable: Number(f.min_billable) || 0, volumetric_divisor: Number(f.volumetric_divisor) || 6000,
      special_surcharge: Number(f.special_surcharge) || 0, delivery_surcharge: Number(f.delivery_surcharge) || 0,
      destination_fees: Number(f.destination_fees) || 0,
      min_cbm: Number(f.min_cbm) || 0, doc_fees: Number(f.doc_fees) || 0, customs_fees: Number(f.customs_fees) || 0,
      pallet_price: Number(f.pallet_price) || 0, carton_price: Number(f.carton_price) || 0,
      weight_tiers: f.mode === "air" ? tiers : [],
      cbm_tiers: f.mode === "sea" ? tiers : [],
    };
    await api.post("/partner/routes", payload); toast.success("Route ajoutée"); setOpen(false); setF(empty); onChange();
  };
  const del = async (id) => { await api.delete(`/partner/routes/${id}`); onChange(); };
  const unit = f.mode === "air" ? "kg" : "CBM";

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="mb-4 rounded-sm bg-accent text-white gap-1" data-testid="add-route-btn"><Plus className="h-4 w-4" /> Ajouter une route</Button></DialogTrigger>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Nouvelle route</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Pays origine</Label><Input data-testid="route-origin-country" className="rounded-sm" value={f.origin_country} onChange={(e) => setF({ ...f, origin_country: e.target.value })} /></div>
            <div><Label>Ville origine</Label><Input className="rounded-sm" value={f.origin_city} onChange={(e) => setF({ ...f, origin_city: e.target.value })} /></div>
            <div><Label>Pays destination</Label><Input className="rounded-sm" value={f.dest_country} onChange={(e) => setF({ ...f, dest_country: e.target.value })} /></div>
            <div><Label>Ville destination</Label><Input className="rounded-sm" value={f.dest_city} onChange={(e) => setF({ ...f, dest_city: e.target.value })} /></div>
            <div><Label>Mode</Label>
              <Select value={f.mode} onValueChange={(v) => setF({ ...f, mode: v })}>
                <SelectTrigger data-testid="route-mode" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="air">Aérien</SelectItem><SelectItem value="sea">Maritime</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Devise</Label><Input className="rounded-sm" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} /></div>
            <div><Label>Départs</Label><Input className="rounded-sm" placeholder="Mardi et vendredi" value={f.departures} onChange={(e) => setF({ ...f, departures: e.target.value })} /></div>
            <div><Label>Délai (jours)</Label><Input className="rounded-sm" value={f.delay_days} onChange={(e) => setF({ ...f, delay_days: e.target.value })} /></div>
          </div>

          {/* Paliers de prix */}
          <div className="border border-border p-3">
            <div className="flex items-center justify-between">
              <Label className="font-bold">{t("route.tiers")} ({f.mode === "air" ? "€/kg" : "€/CBM"})</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-sm gap-1" data-testid="add-tier-btn" onClick={addTier}><Plus className="h-3.5 w-3.5" /> {t("route.addTier")}</Button>
            </div>
            <div className="mt-2 space-y-2">
              {f.tiers.map((tr, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                  <div><Label className="text-xs text-muted-foreground">{t("route.from")} ({unit})</Label><Input data-testid={`tier-min-${i}`} type="number" className="rounded-sm h-9" value={tr.min} onChange={(e) => setTier(i, "min", e.target.value)} /></div>
                  <div><Label className="text-xs text-muted-foreground">{t("route.to")}</Label><Input data-testid={`tier-max-${i}`} type="number" className="rounded-sm h-9" value={tr.max} onChange={(e) => setTier(i, "max", e.target.value)} /></div>
                  <div><Label className="text-xs text-muted-foreground">{t("route.price")} {curSym(f.currency)}</Label><Input data-testid={`tier-price-${i}`} type="number" className="rounded-sm h-9" value={tr.price} onChange={(e) => setTier(i, "price", e.target.value)} /></div>
                  <Button type="button" size="icon" variant="ghost" className="text-destructive mt-4 h-9 w-9" onClick={() => removeTier(i)} disabled={f.tiers.length === 1}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>

          {f.mode === "air" ? (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("route.minBillable")}</Label><Input data-testid="route-min-billable" type="number" className="rounded-sm" value={f.min_billable} onChange={(e) => setF({ ...f, min_billable: e.target.value })} /></div>
              <div><Label>{t("route.volDivisor")}</Label><Input type="number" className="rounded-sm" value={f.volumetric_divisor} onChange={(e) => setF({ ...f, volumetric_divisor: e.target.value })} /></div>
              <div><Label>{t("route.specialSurcharge")}</Label><Input type="number" className="rounded-sm" value={f.special_surcharge} onChange={(e) => setF({ ...f, special_surcharge: e.target.value })} /></div>
              <div><Label>{t("route.deliverySurcharge")}</Label><Input type="number" className="rounded-sm" value={f.delivery_surcharge} onChange={(e) => setF({ ...f, delivery_surcharge: e.target.value })} /></div>
              <div><Label>{t("route.destFees")}</Label><Input type="number" className="rounded-sm" value={f.destination_fees} onChange={(e) => setF({ ...f, destination_fees: e.target.value })} /></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("route.minCbm")}</Label><Input data-testid="route-min-cbm" type="number" className="rounded-sm" value={f.min_cbm} onChange={(e) => setF({ ...f, min_cbm: e.target.value })} /></div>
              <div><Label>{t("route.docFees")}</Label><Input type="number" className="rounded-sm" value={f.doc_fees} onChange={(e) => setF({ ...f, doc_fees: e.target.value })} /></div>
              <div><Label>{t("route.customsFees")}</Label><Input type="number" className="rounded-sm" value={f.customs_fees} onChange={(e) => setF({ ...f, customs_fees: e.target.value })} /></div>
              <div><Label>{t("route.deliverySurcharge")}</Label><Input type="number" className="rounded-sm" value={f.delivery_surcharge} onChange={(e) => setF({ ...f, delivery_surcharge: e.target.value })} /></div>
              <div><Label>{t("route.palletPrice")}</Label><Input data-testid="route-pallet-price" type="number" className="rounded-sm" value={f.pallet_price} onChange={(e) => setF({ ...f, pallet_price: e.target.value })} /></div>
              <div><Label>{t("route.cartonPrice")}</Label><Input data-testid="route-carton-price" type="number" className="rounded-sm" value={f.carton_price} onChange={(e) => setF({ ...f, carton_price: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button className="rounded-sm bg-accent text-white" data-testid="save-route-btn" onClick={add}>{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="space-y-2">
        {routes.map((rt) => {
          const Mode = rt.mode === "air" ? Plane : Ship;
          const tiers = (rt.mode === "air" ? rt.weight_tiers : rt.cbm_tiers) || [];
          const u = rt.mode === "air" ? "kg" : "CBM";
          const minPrice = tiers.length ? Math.min(...tiers.map((x) => x.price)) : 0;
          return (
            <div key={rt.id} className="border border-border bg-card p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-medium flex-wrap"><Mode className="h-4 w-4 text-accent" /> {rt.origin_city} <ArrowRight className="h-4 w-4" /> {rt.dest_city} <span className="text-accent font-bold ml-1">{t("results.from")} {minPrice} {curSym(rt.currency)}/{u}</span> {!rt.active && <Badge className="rounded-sm bg-amber-100 text-amber-700 ml-1">Inactive</Badge>}</span>
                <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(rt.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5 text-xs text-muted-foreground">
                {tiers.map((tr, i) => (
                  <span key={i} className="border border-border px-1.5 py-0.5">{tr.min}–{tr.max || "∞"} {u}: {tr.price} {curSym(rt.currency)}</span>
                ))}
                {rt.pallet_price > 0 && <span className="border border-border px-1.5 py-0.5">{t("route.palletPrice")}: {rt.pallet_price} {curSym(rt.currency)}</span>}
                {rt.carton_price > 0 && <span className="border border-border px-1.5 py-0.5">{t("route.cartonPrice")}: {rt.carton_price} {curSym(rt.currency)}</span>}
              </div>
            </div>
          );
        })}
        {routes.length === 0 && <p className="text-sm text-muted-foreground">Aucune route. Ajoutez vos corridors desservis.</p>}
      </div>
    </div>
  );
}

function ProfileForm({ profile, onSaved }) {
  const { t } = useI18n();
  const [f, setF] = useState({ ...profile });
  const save = async () => {
    await api.put("/partner/profile", {
      company_name: f.company_name, country: f.country, city: f.city, receiving_address: f.receiving_address,
      phone: f.phone || "", logo: f.logo || "", description: f.description || "", depot_hours: f.depot_hours || "",
      accepted_goods: f.accepted_goods || "", forbidden_goods: f.forbidden_goods || "",
      special_conditions: f.special_conditions || "",
      customs_included: !!f.customs_included, final_delivery_included: !!f.final_delivery_included,
    });
    toast.success("Profil enregistré"); onSaved();
  };
  return (
    <div className="border border-border bg-card p-5 max-w-2xl space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Nom commercial</Label><Input className="rounded-sm" value={f.company_name || ""} onChange={(e) => setF({ ...f, company_name: e.target.value })} /></div>
        <div><Label>{t("profile.phone")}</Label><Input data-testid="profile-phone" type="tel" className="rounded-sm" value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
        <div><Label>Pays</Label><Input className="rounded-sm" value={f.country || ""} onChange={(e) => setF({ ...f, country: e.target.value })} /></div>
        <div><Label>Ville</Label><Input className="rounded-sm" value={f.city || ""} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
        <div><Label>Horaires dépôt</Label><Input className="rounded-sm" value={f.depot_hours || ""} onChange={(e) => setF({ ...f, depot_hours: e.target.value })} /></div>
        <div><Label>{t("profile.logo")}</Label><Input className="rounded-sm" placeholder="https://…" value={f.logo || ""} onChange={(e) => setF({ ...f, logo: e.target.value })} /></div>
      </div>
      <div><Label className="flex items-center gap-1"><MapPin className="h-4 w-4" /> Adresse de réception</Label><Input data-testid="profile-address" className="rounded-sm" value={f.receiving_address || ""} onChange={(e) => setF({ ...f, receiving_address: e.target.value })} /></div>
      <div><Label>Description</Label><Textarea className="rounded-sm" value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Marchandises acceptées</Label><Textarea className="rounded-sm" value={f.accepted_goods || ""} onChange={(e) => setF({ ...f, accepted_goods: e.target.value })} /></div>
        <div><Label>Marchandises interdites</Label><Textarea className="rounded-sm" value={f.forbidden_goods || ""} onChange={(e) => setF({ ...f, forbidden_goods: e.target.value })} /></div>
      </div>
      <div><Label>{t("profile.specialConditions")}</Label><Textarea data-testid="profile-conditions" className="rounded-sm" value={f.special_conditions || ""} onChange={(e) => setF({ ...f, special_conditions: e.target.value })} /></div>
      <div className="flex flex-wrap gap-5">
        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
          <input data-testid="profile-customs" type="checkbox" className="h-4 w-4 accent-[#FF6600]" checked={!!f.customs_included} onChange={(e) => setF({ ...f, customs_included: e.target.checked })} />
          {t("profile.customsIncluded")}
        </label>
        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
          <input data-testid="profile-delivery" type="checkbox" className="h-4 w-4 accent-[#FF6600]" checked={!!f.final_delivery_included} onChange={(e) => setF({ ...f, final_delivery_included: e.target.checked })} />
          {t("profile.deliveryIncluded")}
        </label>
      </div>
      <Button data-testid="save-profile-btn" className="rounded-sm bg-accent text-white" onClick={save}>{t("common.save")}</Button>
    </div>
  );
}
